// y-protocols (lib0) framing for the bytes inside `DocPayload.payload`.
//
// The wire format is the standard y-protocols / y-websocket sync v1 message
// set (protocol spec §y-protocols framing), byte-compatible with the
// server's `yrs::sync` implementation:
//
//   payload := varUint msgType, body
//     msgType 0 (sync):      varUint syncType, varUint-length-prefixed buffer
//         syncType 0 = SyncStep1(state vector)
//         syncType 1 = SyncStep2(update, v1)
//         syncType 2 = Update(update, v1)
//     msgType 1 (awareness): varUint-length-prefixed awareness update
//     msgType 3 (awareness query): no body
//
// We deliberately reuse the `y-protocols` npm codecs (sync/awareness) rather
// than hand-rolling lib0 framing — the same decision the server made with
// `yrs::sync` (decisions/10-sync-core.md).

import * as encoding from "lib0/encoding";
import * as decoding from "lib0/decoding";
import * as syncProtocol from "y-protocols/sync";
import * as awarenessProtocol from "y-protocols/awareness";
import type * as Y from "yjs";

export const MSG_SYNC = 0;
export const MSG_AWARENESS = 1;
export const MSG_AWARENESS_QUERY = 3;

/** Re-export so callers construct one Awareness per doc without a second dep. */
export const Awareness = awarenessProtocol.Awareness;
export type Awareness = awarenessProtocol.Awareness;

/** Encode a sync-step-1 frame carrying the doc's current state vector. */
export function encodeSyncStep1(doc: Y.Doc): Uint8Array {
  const enc = encoding.createEncoder();
  encoding.writeVarUint(enc, MSG_SYNC);
  syncProtocol.writeSyncStep1(enc, doc);
  return encoding.toUint8Array(enc);
}

/**
 * Encode a sync-step-2 frame: the doc's state diffed against `sv` (a v1
 * state vector), or the full state when `sv` is omitted.
 */
export function encodeSyncStep2(doc: Y.Doc, sv?: Uint8Array): Uint8Array {
  const enc = encoding.createEncoder();
  encoding.writeVarUint(enc, MSG_SYNC);
  syncProtocol.writeSyncStep2(enc, doc, sv);
  return encoding.toUint8Array(enc);
}

/** Encode an incremental-update frame (msgType 0 / syncType 2). */
export function encodeUpdate(update: Uint8Array): Uint8Array {
  const enc = encoding.createEncoder();
  encoding.writeVarUint(enc, MSG_SYNC);
  syncProtocol.writeUpdate(enc, update);
  return encoding.toUint8Array(enc);
}

/** Encode an awareness update frame for the given client ids. */
export function encodeAwareness(
  awareness: Awareness,
  clients: number[],
): Uint8Array {
  const enc = encoding.createEncoder();
  encoding.writeVarUint(enc, MSG_AWARENESS);
  encoding.writeVarUint8Array(
    enc,
    awarenessProtocol.encodeAwarenessUpdate(awareness, clients),
  );
  return encoding.toUint8Array(enc);
}

/** The category of a decoded inbound frame. */
export type FrameKind = "sync" | "awareness" | "awarenessQuery" | "unknown";

export interface ReadResult {
  kind: FrameKind;
  /**
   * A reply the caller MUST send back on the same doc: a sync step 2
   * answering the server's step 1, or an awareness snapshot answering an
   * awareness query. Undefined when no reply is owed.
   */
  reply?: Uint8Array;
}

/**
 * Decode one inbound `DocPayload.payload`, applying it to `doc`/`awareness`
 * with `origin` as the Yjs transaction origin (so the DocStore can tell a
 * remote apply from a local edit and avoid echoing it back). Returns any
 * reply owed to the server.
 */
export function readMessage(
  doc: Y.Doc,
  awareness: Awareness,
  payload: Uint8Array,
  origin: unknown,
): ReadResult {
  const decoder = decoding.createDecoder(payload);
  const encoder = encoding.createEncoder();
  const msgType = decoding.readVarUint(decoder);
  switch (msgType) {
    case MSG_SYNC: {
      encoding.writeVarUint(encoder, MSG_SYNC);
      // readSyncMessage reads the syncType and, for a step 1, writes our
      // step 2 (the tail the server is missing) into `encoder`; for step 2 /
      // update it applies to `doc` with `origin` and writes nothing.
      syncProtocol.readSyncMessage(decoder, encoder, doc, origin);
      // >1 byte means more than the bare msgType prefix — a reply is owed.
      if (encoding.length(encoder) > 1) {
        return { kind: "sync", reply: encoding.toUint8Array(encoder) };
      }
      return { kind: "sync" };
    }
    case MSG_AWARENESS: {
      awarenessProtocol.applyAwarenessUpdate(
        awareness,
        decoding.readVarUint8Array(decoder),
        origin,
      );
      return { kind: "awareness" };
    }
    case MSG_AWARENESS_QUERY: {
      const states = awareness.getStates();
      if (states.size > 0) {
        return {
          kind: "awarenessQuery",
          reply: encodeAwareness(awareness, [...states.keys()]),
        };
      }
      return { kind: "awarenessQuery" };
    }
    default:
      return { kind: "unknown" };
  }
}
